import { useReducer } from 'react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { type ExistingEmployeesByEmail } from 'modules/employees/lib/validateEmailForCreation'
import { WizardData } from 'modules/employees/pages/ImportProcessPage/components/ImportProcessWizard/WizardData'

import { MappedValues } from './components/MapValuesModal'
import { type ReducerState, reducer } from './editableContentReducer'

interface SetInitialData {
  fields: ReducerState['fields']
  editedFileContent: ReducerState['editedFileContent']
  mappedFields: ReducerState['mappedFields']
  legalEntities: ReducerState['legalEntities']
  locations: ReducerState['locations']
  teams: ReducerState['teams']
  dateFormats: ReducerState['dateFormats']
  availableRoleLevels: ReducerState['availableRoleLevels']
  availableNationalities: ReducerState['availableNationalities']
  availableCountries: ReducerState['availableCountries']
  existingEmployeesByEmail: ExistingEmployeesByEmail
}

interface Props {
  wizardData: WizardData
  onDataChanged: (data: WizardData) => void
  existingEmployeesByEmail: ExistingEmployeesByEmail
}

const useEditableContent = ({ wizardData, onDataChanged, existingEmployeesByEmail }: Props) => {
  const [state, dispatch] = useReducer(reducer, { existingEmployeesByEmail })

  useUnsafeEffect(() => {
    if (!state?.rows) return

    onDataChanged({
      ...wizardData,
      editedFileContent: {
        headers: wizardData?.editedFileContent?.headers,
        rows: state.rows?.map((row) => {
          return row?.cells?.map((cell) => cell?.value || '') || []
        }),
      },
      cellsWithError: !!state?.cellCoordinatesWithError?.length,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  const isCellMapped = (cellIndex: number) => {
    return wizardData.mappedFields?.some((mappedField) => mappedField.columnIndex === cellIndex)
  }

  const rowsFilteredByMappedFields = state.rows?.map((row) => {
    const cells = row?.cells?.map((cellValue, cellIndex) => {
      if (isCellMapped(cellIndex)) return cellValue

      return undefined
    })

    return {
      index: row?.index,
      selected: row?.selected ?? false,
      cells,
    }
  })

  const setInitialState = (initialData: SetInitialData) => {
    dispatch({
      type: 'setInitialData',
      newState: { ...initialData, existingEmployeesByEmail },
    })
  }

  const editCell = (rowIndex: number, columnIndex: number, value: string, isValid: boolean) => {
    dispatch({
      type: 'editCell',
      rowIndex,
      columnIndex,
      newValue: value,
      isValid,
    })
  }

  const mapValues = (mappedValues: MappedValues, fieldName: string) => {
    dispatch({
      type: 'mapFieldValues',
      fieldName,
      mappedValues,
    })
  }

  const toggleSelectRow = (rowIndex: number) => {
    dispatch({
      type: 'toggleSelectRow',
      rowIndex,
    })
  }

  const selectAllRows = (selected: boolean) => {
    dispatch({
      type: 'selectAllRows',
      selected,
    })
  }

  return {
    stepData: state,
    rowsFilteredByMappedFields,
    setInitialState,
    editCell,
    mapValues,
    toggleSelectRow,
    selectAllRows,
  }
}

export default useEditableContent
